/**
 * CALENDAR WORKLOAD — "what production workload is committed to this shop
 * day?" — answered from the SAME production truth the Dayboard and OptiBoard
 * use (fix/foh-optiboard-calendar-truth).
 *
 * The Calendar used to decide for itself from `status IN (Active, Paused)` and
 * `parking_lot`: it counted estimates on the day they were created, dropped
 * every parked ticket (scheduled_but_parked AND held_in_parking) from the day's
 * load, and never counted a specialty job (it read job columns it never
 * selected).
 *
 * The law now — existing doctrine, reused, not invented:
 *
 *   counts toward a day  ⇔  consumesFutureScheduleCapacity(ticket)
 *                          (dualCapacityModel: every production-relevant
 *                           state — scheduled, scheduled_but_parked,
 *                           active_in_bay, held_in_bay, held_in_parking)
 *
 * Being parked does not make booked work disappear; completed, picked-up,
 * deleted, archived, parked-without-a-plan and estimates commit no production
 * time. Custody, location and advisor acceptance are not this question.
 *
 * BOOK TIME (`bookedMinutes`) stays the ticket's raw `predicted_minutes` —
 * what was sold/planned. EFFECTIVE LOAD (utilization, OBLI) is a different
 * number, computed by the Dayboard's own mapping (dayEffectiveLoad.ts) so the
 * two surfaces never disagree about how loaded a day is.
 */
import { consumesFutureScheduleCapacity } from '@/utils/dualCapacityModel';
import type { ProductionStateTicketShape } from '@/utils/ticketProductionState';
import { getShopDayLabel } from '@/utils/shopTime';

/**
 * What the Calendar downloads: the canonical production fields plus what it
 * measures. `status IN (Active, Paused)` stays as a cheap pre-filter; a
 * `Cancelled` row classifies 'deleted' canonically anyway, so it never counts
 * even without it. The canonical classification decides everything else.
 */
export const CALENDAR_TICKET_COLUMNS =
  'id, shop_id, status, type, is_estimate, deleted_at, archived_at, picked_up_at, completed_at, paused_at, '
  + 'timer_status, actually_started, bay_id, parking_lot, parking_spot_id, assigned_tech_id, scheduled_tech_id, '
  + 'scheduled_date, scheduled_start_time, start_time, predicted_minutes, '
  // what the shared effective-load mapping reads (live time + book fallback)
  + 'actual_start_time, run_long_minutes, run_long_opti_baseline, '
  + 'ticket_jobs(minutes_override, notes, separate_time, diagnostic_completed_at, '
  + 'job:jobs(proficiency_category, bay_requirement, category))';

/**
 * Core-100 R4 W5 (CORE-AX S/Rel): the Calendar no longer reads `tickets` directly. The server read model
 * `calendar_workload_tickets(shop, from, to, time_zone)` (migration 20270206023500, SECURITY INVOKER — the caller's
 * RLS) returns exactly the columns above (ticket_jobs as the same nested shape) for a BOUNDED period — at most
 * {@link CALENDAR_MAX_RANGE_DAYS} days — with terminal facts excluded at the source and the day decided in the shop's
 * zone. The column list stays here as the shape's contract (the runtime proof compares the two).
 */
export const CALENDAR_WORKLOAD_RPC = 'calendar_workload_tickets';
export const CALENDAR_MAX_RANGE_DAYS = 62;

export type CalendarTicket = ProductionStateTicketShape & {
  id?: string;
  type?: string | null;
  start_time?: string | null;
  predicted_minutes?: number | null;
  ticket_jobs?: Array<{ job?: { bay_requirement?: string | null; category?: string | null } | null }> | null;
};

/** Does this ticket commit production time to its shop day? */
export function countsTowardDayWorkload(ticket: CalendarTicket): boolean {
  return consumesFutureScheduleCapacity(ticket);
}

/** The shop day a ticket's work is booked for (scheduled_date; else its start in the shop's zone). */
export function calendarDayOf(ticket: CalendarTicket, shopTimezone: string): string | null {
  if (ticket.scheduled_date) return ticket.scheduled_date;
  return ticket.start_time ? getShopDayLabel(ticket.start_time, shopTimezone) : null;
}

/** ADAS / alignment / specialty-bay work. */
export function hasSpecialtyJob(ticket: CalendarTicket): boolean {
  return (ticket.ticket_jobs ?? []).some((tj) => {
    const job = tj?.job;
    if (!job) return false;
    return job.bay_requirement === 'ADAS'
      || job.bay_requirement === 'Alignment'
      || job.bay_requirement === 'Specialty'
      || job.category?.toLowerCase() === 'adas'
      || job.category?.toLowerCase() === 'alignment';
  });
}

export interface DayWorkload<T> {
  tickets: T[];
  ticketCount: number;
  bookedMinutes: number;
  waiterCount: number;
  specialtyCount: number;
}

/** The production workload committed to `dateStr` (yyyy-MM-dd, shop day). */
export function dayWorkload<T extends CalendarTicket>(tickets: readonly T[], dateStr: string, shopTimezone: string): DayWorkload<T> {
  const day = tickets.filter((t) => countsTowardDayWorkload(t) && calendarDayOf(t, shopTimezone) === dateStr);
  return {
    tickets: day,
    ticketCount: day.length,
    bookedMinutes: day.reduce((sum, t) => sum + (t.predicted_minutes || 0), 0),
    waiterCount: day.filter((t) => t.type === 'Waiter').length,
    specialtyCount: day.filter(hasSpecialtyJob).length,
  };
}

/**
 * Core-100 R4 (CORE-AX): the technician columns the Calendar's capacity and effective load read — a narrow
 * select (it read `*`: pay, contact and profile columns it never used).
 */
export const CALENDAR_TECHNICIAN_COLUMNS =
  'id, name, hours, time_off, shift_start, shift_end, recurring_breaks, non_recurring_breaks, efficiency_percentage, buffer_percentage, employment_status, hire_date';
